import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBriefcase,
    faUser,
    faEnvelope,
    faPhone,
    faGraduationCap,
    faFileLines,
    faPaperPlane,
    faCircleCheck,
    faTriangleExclamation,
    faSpinner,
    faLeaf,
} from '@fortawesome/free-solid-svg-icons';
import './Forms.css';

const API_URL =
    'https://m9luhuc58h.execute-api.ap-south-1.amazonaws.com/prod/careers';

const MAX_RESUME_BYTES = 5 * 1024 * 1024;
const ACCEPTED_RESUME_TYPES = '.pdf,.doc,.docx';

const initialFormState = {
    name: '',
    email: '',
    phone: '',
    position: '',
    qualification: '',
    experience: '',
    message: '',
};

const REASONS = [
    'A respected institution under PNRC, INC and Baba Farid University of Health Sciences.',
    'A supportive teaching environment with modern facilities.',
    'Real opportunities for professional growth and skill development.',
    'A collaborative culture rooted in service and integrity.',
];

const Careers = () => {
    const [formData, setFormData] = useState(initialFormState);
    const [resumeFile, setResumeFile] = useState(null);
    const [status, setStatus] = useState({ state: 'idle', message: '' });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            setResumeFile(null);
            return;
        }
        if (file.size > MAX_RESUME_BYTES) {
            setStatus({
                state: 'error',
                message: 'Resume must be 5 MB or smaller. Please upload a smaller file.',
            });
            e.target.value = '';
            setResumeFile(null);
            return;
        }
        setStatus({ state: 'idle', message: '' });
        setResumeFile(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!resumeFile) {
            setStatus({ state: 'error', message: 'Please attach your resume before submitting.' });
            return;
        }
        setStatus({ state: 'submitting', message: '' });

        try {
            const presignRes = await fetch(`${API_URL}/presign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filename: resumeFile.name, fileType: resumeFile.type || 'application/octet-stream' }),
            });
            if (!presignRes.ok) {
                const text = await presignRes.text().catch(() => '');
                throw new Error(`Presign request failed: ${presignRes.status} ${text}`);
            }
            const presignJson = await presignRes.json();
            const uploadUrl = presignJson.url || presignJson.uploadUrl || presignJson.presignedUrl;
            const key = presignJson.key;
            if (!uploadUrl || !key) throw new Error('Invalid presign response');

            const uploadRes = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': resumeFile.type || 'application/octet-stream' },
                body: resumeFile,
            });
            if (!(uploadRes.ok || uploadRes.status === 200 || uploadRes.status === 204)) {
                const text = await uploadRes.text().catch(() => '');
                throw new Error(`Upload failed: ${uploadRes.status} ${text}`);
            }

            const payload = {
                ...formData,
                resumeName: resumeFile.name,
                resumeType: resumeFile.type || 'application/octet-stream',
                resumeKey: key,
            };
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const text = await response.text().catch(() => '');
                throw new Error(`API error: ${response.status} ${text}`);
            }

            setStatus({
                state: 'success',
                message: 'Thank you! Your application is in. Our team will be in touch shortly.',
            });
            setFormData(initialFormState);
            setResumeFile(null);
            const fileInput = document.getElementById('resume');
            if (fileInput) fileInput.value = '';
        } catch (err) {
            setStatus({
                state: 'error',
                message: 'We could not submit your application right now. Please try again in a moment.',
            });
        }
    };

    const isSubmitting = status.state === 'submitting';

    return (
        <>
            <section className="page-hero">
                <span className="blob" aria-hidden="true" />
                <div className="container page-hero-inner">
                    <span className="page-hero-icon">
                        <FontAwesomeIcon icon={faBriefcase} />
                    </span>
                    <p className="eyebrow">Join our faculty</p>
                    <h1 className="page-hero-title">Build your career with us</h1>
                    <p className="page-hero-lead">
                        We're hiring across all four colleges — nursing, allied healthcare,
                        management and law. Tell us about yourself and attach your resume;
                        we'd love to hear from you.
                    </p>
                </div>
            </section>

            <section className="form-section">
                <div className="container form-grid">
                    <aside className="form-side">
                        <div className="form-side-body">
                            <h2 className="form-side-title">Why work with us?</h2>
                            <ul className="why-list">
                                {REASONS.map((r) => (
                                    <li key={r}>
                                        <FontAwesomeIcon icon={faLeaf} />
                                        {r}
                                    </li>
                                ))}
                            </ul>
                            <div className="form-side-contact">
                                <a href="mailto:shriguruarjundevjiinstitute@gmail.com">
                                    <FontAwesomeIcon icon={faEnvelope} />
                                    shriguruarjundevjiinstitute@gmail.com
                                </a>
                                <a href="tel:+919899923387">
                                    <FontAwesomeIcon icon={faPhone} />
                                    +91-9899923387
                                </a>
                            </div>
                        </div>
                    </aside>

                    <div className="form-card">
                        <h2 className="form-title">Application form</h2>
                        <p className="form-sub">
                            Fields marked <span className="req">*</span> are required.
                        </p>

                        {status.state === 'success' && (
                            <div className="alert alert--ok" role="status">
                                <FontAwesomeIcon icon={faCircleCheck} />
                                <span>{status.message}</span>
                            </div>
                        )}
                        {status.state === 'error' && (
                            <div className="alert alert--err" role="alert">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                <span>{status.message}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate>
                            <div className="grid-2">
                                <div className="field">
                                    <label htmlFor="name" className="field-label">
                                        <FontAwesomeIcon icon={faUser} /> Full name <span className="req">*</span>
                                    </label>
                                    <input id="name" name="name" className="input" placeholder="Enter your full name"
                                        value={formData.name} onChange={handleChange} required />
                                </div>
                                <div className="field">
                                    <label htmlFor="email" className="field-label">
                                        <FontAwesomeIcon icon={faEnvelope} /> Email <span className="req">*</span>
                                    </label>
                                    <input id="email" name="email" type="email" className="input" placeholder="you@example.com"
                                        value={formData.email} onChange={handleChange} required />
                                </div>
                                <div className="field">
                                    <label htmlFor="phone" className="field-label">
                                        <FontAwesomeIcon icon={faPhone} /> Phone <span className="req">*</span>
                                    </label>
                                    <input id="phone" name="phone" type="tel" className="input" placeholder="+91-XXXXXXXXXX"
                                        value={formData.phone} onChange={handleChange} required />
                                </div>
                                <div className="field">
                                    <label htmlFor="position" className="field-label">
                                        <FontAwesomeIcon icon={faBriefcase} /> Position <span className="req">*</span>
                                    </label>
                                    <input id="position" name="position" className="input" placeholder="e.g. Nursing Tutor"
                                        value={formData.position} onChange={handleChange} required />
                                </div>
                                <div className="field">
                                    <label htmlFor="qualification" className="field-label">
                                        <FontAwesomeIcon icon={faGraduationCap} /> Qualification <span className="req">*</span>
                                    </label>
                                    <input id="qualification" name="qualification" className="input" placeholder="e.g. M.Sc Nursing"
                                        value={formData.qualification} onChange={handleChange} required />
                                </div>
                                <div className="field">
                                    <label htmlFor="experience" className="field-label">Years of experience</label>
                                    <input id="experience" name="experience" className="input" placeholder="e.g. 3 years"
                                        value={formData.experience} onChange={handleChange} />
                                </div>
                                <div className="field span-2">
                                    <label htmlFor="message" className="field-label">Cover note / message</label>
                                    <textarea id="message" name="message" rows="4" className="input"
                                        placeholder="Briefly tell us why you'd like to join us"
                                        value={formData.message} onChange={handleChange} />
                                </div>
                                <div className="field span-2">
                                    <label htmlFor="resume" className="field-label">
                                        <FontAwesomeIcon icon={faFileLines} /> Upload resume <span className="req">*</span>
                                    </label>
                                    <input id="resume" name="resume" type="file" accept={ACCEPTED_RESUME_TYPES}
                                        className="input" onChange={handleFileChange} required />
                                    <span className="field-hint">Accepted formats: PDF, DOC, DOCX. Max size 5 MB.</span>
                                </div>
                            </div>
                            <button type="submit" disabled={isSubmitting} className="btn btn--primary form-submit">
                                {isSubmitting ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Submitting…</>
                                ) : (
                                    <><FontAwesomeIcon icon={faPaperPlane} /> Submit application</>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Careers;
